import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  Button,
  Checkbox,
  Dropdown,
  DropdownElement,
  Icon,
  Input,
  OptionGroup,
  ToggleHeight,
} from '@column/column-ui-kit';

import {
  BeneficialOwnerFormValues,
  BusinessEntityFormValues,
  IdentificationDocument,
  createDefaultBeneficialOwner,
} from '../_util/useEntityForm';
import { AddressField, DateInput, HiddenInput, RenderFields, SectionHeader } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';
import { Card, Flex, Grid, Inner } from '~/styles';

const EmployerIdentificationNumberControl = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
`;

const PendingCheckbox = styled(Checkbox)`
  align-items: center;
`;

const useIsCitizenDefaults = (form: ReturnType<typeof useFormContext>, hasId: boolean) => {
  useEffect(() => {
    const beneficialOwnerFields: BeneficialOwnerFormValues[] = form.getValues('beneficialOwners') ?? [];
    beneficialOwnerFields.forEach((_, index) => {
      const current = form.getValues(`beneficialOwners.${index}.temporaryData.isCitizen`);
      if (typeof current !== 'boolean') {
        const ssn = form.getValues(`beneficialOwners.${index}.ssn`);
        const passport = form.getValues(`beneficialOwners.${index}.passport.number`);
        const driversLicense = form.getValues(`beneficialOwners.${index}.driversLicense.number`);
        const nationalId = form.getValues(`beneficialOwners.${index}.nationalId.number`);
        if (!hasId) {
          form.setValue(`beneficialOwners.${index}.temporaryData.isCitizen`, true);
          return;
        }
        if (ssn) {
          form.setValue(`beneficialOwners.${index}.temporaryData.isCitizen`, true);
        } else if (passport || driversLicense || nationalId) {
          form.setValue(`beneficialOwners.${index}.temporaryData.isCitizen`, false);
        } else {
          form.setValue(`beneficialOwners.${index}.temporaryData.isCitizen`, true);
        }
      }
    });
  }, [form, hasId, JSON.stringify(form.getValues('beneficialOwners'))]);
};

export const BusinessEntity: React.FC<{
  id?: string;
  disableForm?: boolean;
  isRoot?: boolean;
}> = (props) => {
  const form = useFormContext();
  const { countries, states } = useAddress({ types: ['state', 'country'] });
  const entityBusinessAddressStateRef = useRef<DropdownElement>(null);
  const beneficialOwnerStateRef = useRef<DropdownElement>(null);
  const [businessAddressSearchValue, setBusinessAddressSearchValue] = useState('');
  const [beneficialOwnerAddressSearchValues, setBeneficialOwnerAddressSearchValues] = useState<string[]>([]);
  const [beneficialOwnerOpen, setBeneficialOwnerOpen] = useState<number[]>([0]);
  const beneficialOwnerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const beneficialOwnerFirstFieldRefs = useRef<({ focus: () => void } | null)[]>([]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'beneficialOwners',
  });

  useIsCitizenDefaults(form, Boolean(props.id));

  const requiresUSBusinessAddressRegion = form.watch('address.countryCode') === 'US';
  const isEinPending = form.watch('einPending') === true;

  const handleToggle = (index: number) => {
    setBeneficialOwnerOpen((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const businessSections: SectionWithFields<BusinessEntityFormValues, undefined>[] = [
    {
      headline: 'Business Information',
      fields: [
        {
          id: 'businessName',
          label: 'Business Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Business Name"
              hasError={isTouched && !!error}
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'ein',
          label: 'Employer Identification Number',
          children: ({ value, onChange, onBlur, ref }) => {
            const showEinPending = !props.isRoot && !props.id && (!props.disableForm || isEinPending);

            return (
              <EmployerIdentificationNumberControl>
                <HiddenInput
                  value={(value as string) ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  placeholder="EIN"
                  defaultIsVisible={!props.id}
                  isDisabled={props.disableForm || (!props.id && isEinPending)}
                  readOnly={false}
                  showCopy={false}
                  visibilityLabel="Employer Identification Number"
                />
                {showEinPending && (
                  <PendingCheckbox
                    isChecked={isEinPending}
                    isDisabled={props.disableForm}
                    label="Pending"
                    onCheckedChange={(checked) => {
                      if (props.disableForm) return;

                      form.setValue('einPending', checked, { shouldDirty: true, shouldValidate: true });
                      if (checked) {
                        form.setValue('ein', '', { shouldDirty: true, shouldValidate: true });
                      }
                    }}
                  />
                )}
              </EmployerIdentificationNumberControl>
            );
          },
        },
        {
          id: 'website',
          label: 'Website',
          children: ({ value, onChange, onBlur, ref }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Website"
              isDisabled={props.disableForm}
            />
          ),
        },
      ],
    },
    {
      headline: 'Address',
      fields: [
        {
          id: 'address',
          label: 'Search Address',
          children: () => (
            <AddressField
              onChange={(a) => {
                form.setValue('address.line1', a.line1);
                form.setValue('address.line2', a.line2);
                form.setValue('address.city', a.city);
                form.setValue('address.state', a.state);
                form.setValue('address.postalCode', a.postalCode);
                form.setValue('address.countryCode', a.countryCode);
                setBusinessAddressSearchValue('');
                if (!a.state) {
                  entityBusinessAddressStateRef.current?.open();
                }
              }}
              justUS
              isDisabled={props.disableForm}
              value={businessAddressSearchValue}
              onValueChange={setBusinessAddressSearchValue}
            />
          ),
        },
        {
          id: 'address.line1',
          label: 'Line 1',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 1"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.line2',
          label: 'Line 2',
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 2"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.city',
          label: 'City',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="City"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.state',
          label: 'State',
          rules: requiresUSBusinessAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => {
            const { getValues } = useFormContext();
            const countryCode = getValues('address.countryCode');
            const filteredStates = states.filter((s) => !countryCode || s.country_code === countryCode);
            return (
              <Dropdown
                fullWidth
                ref={entityBusinessAddressStateRef}
                search
                placeholder="Select State..."
                active={(value as string) ?? ''}
                onChange={onChange}
                options={filteredStates.map((s) => ({
                  label: `${s.name} (${s.state_code})`,
                  value: s.state_code,
                }))}
                isDisabled={props.disableForm}
              />
            );
          },
        },
        {
          id: 'address.postalCode',
          label: 'Postal Code',
          rules: requiresUSBusinessAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Postal Code"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.countryCode',
          label: 'Country',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Dropdown
              fullWidth
              search
              placeholder="Select Country..."
              active={(value as string) ?? ''}
              onChange={onChange}
              options={countries.map((c) => ({ label: c.name, value: c.code }))}
              isDisabled={props.disableForm}
            />
          ),
        },
      ],
    },
  ];

  return (
    <>
      <RenderFields sections={businessSections} />
      <SectionHeader text="Beneficial Owners" border />
      <Inner pt={0} px="var(--page-margin)">
        <Flex flexDirection="column" gap={16}>
          {(fields ?? []).map((field, index) => {
            const bo = (form.watch(`beneficialOwners.${index}`) as BeneficialOwnerFormValues | undefined) ?? {};
            const isCitizen = bo?.temporaryData?.isCitizen === true;

            return (
              <Card key={field.id} variant="secondary" ref={(el) => (beneficialOwnerRefs.current[index] = el)}>
                <Flex alignItems="center">
                  <SectionHeader text="Beneficial Owner" counter={index + 1} onClick={() => handleToggle(index)}>
                    {beneficialOwnerOpen.includes(index) ? <Icon.ChevronUpLarge /> : <Icon.ChevronDownLarge />}
                  </SectionHeader>
                  {fields.length > 1 && !props.disableForm && (
                    <Inner pr={16} pl={0} py={0}>
                      <Button variant="secondary-danger" size="small" onClick={() => remove(index)}>
                        Remove
                      </Button>
                    </Inner>
                  )}
                </Flex>
                <ToggleHeight isClose={!beneficialOwnerOpen.includes(index)}>
                  <Inner px={0} pt={0} pb={16}>
                    <RenderFields
                      sections={[
                        {
                          fields: [
                            {
                              id: `beneficialOwners.${index}.firstName`,
                              label: 'First Name',
                              rules: { required: true },
                              children: ({ value, onChange, ref }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="First Name"
                                  isDisabled={props.disableForm}
                                  ref={(el) => {
                                    if (typeof ref === 'function') ref(el);
                                    beneficialOwnerFirstFieldRefs.current[index] = el;
                                  }}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.middleName`,
                              label: 'Middle Name',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Middle Name"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.lastName`,
                              label: 'Last Name',
                              rules: { required: true },
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Last Name"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.dateOfBirth`,
                              label: 'Date of Birth',
                              rules: { required: true },
                              children: ({ value, onChange }) => (
                                <DateInput
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.email`,
                              label: 'Email Address',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Email Address"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.temporaryData.isCitizen`,
                              label: 'Is this person a U.S. Citizen?',
                              children: ({ value, onChange }) => (
                                <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                                  <OptionGroup.Option
                                    isActive={value === true}
                                    onClick={() => onChange(true)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>Yes</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                  <OptionGroup.Option
                                    isActive={value === false}
                                    onClick={() => onChange(false)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>No</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                </OptionGroup>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.ssn`,
                              label: 'Social Security Number',
                              description: 'ITIN is also acceptable.',
                              hide: !isCitizen,
                              children: ({ value, onChange, onBlur, ref }) => (
                                <HiddenInput
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  ref={ref}
                                  placeholder="Social Security Number"
                                  defaultIsVisible={!props.id}
                                  isDisabled={props.disableForm}
                                  readOnly={false}
                                  showCopy={false}
                                  visibilityLabel="Social Security Number"
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.driversLicense`,
                              label: 'Drivers License',
                              hide: isCitizen,
                              children: ({ value, onBlur, ref }) => (
                                <Grid gap={8}>
                                  <Input
                                    value={(value as IdentificationDocument)?.number ?? ''}
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.driversLicense.number`, val)
                                    }
                                    placeholder="Drivers License Number"
                                    onBlur={onBlur}
                                    ref={ref}
                                    isDisabled={props.disableForm}
                                  />
                                  <Dropdown
                                    placeholder="Select Country..."
                                    active={(value as IdentificationDocument)?.countryCode ?? ''}
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.driversLicense.countryCode`, val)
                                    }
                                    options={countries
                                      .filter((c) => c.code !== 'US' && c.code !== 'CA')
                                      .map((c) => ({ label: c.name, value: c.code }))}
                                    fullWidth
                                    isDisabled={props.disableForm}
                                  />
                                </Grid>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.passport`,
                              label: 'Passport',
                              hide: isCitizen,
                              children: ({ value, onBlur, ref }) => (
                                <Grid gap={8}>
                                  <Input
                                    value={(value as IdentificationDocument)?.number ?? ''}
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.passport.number`, val)
                                    }
                                    onBlur={onBlur}
                                    ref={ref}
                                    placeholder="Passport Number"
                                    isDisabled={props.disableForm}
                                  />
                                  <Dropdown
                                    placeholder="Select Country..."
                                    active={(value as IdentificationDocument)?.countryCode ?? ''}
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.passport.countryCode`, val)
                                    }
                                    options={countries
                                      .filter((c) => c.code !== 'US')
                                      .map((c) => ({ label: c.name, value: c.code }))}
                                    fullWidth
                                    isDisabled={props.disableForm}
                                  />
                                </Grid>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.nationalId`,
                              label: 'National ID',
                              hide: isCitizen,
                              children: ({ value, onBlur, ref }) => (
                                <Grid gap={8}>
                                  <Input
                                    value={(value as IdentificationDocument)?.number ?? ''}
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.nationalId.number`, val)
                                    }
                                    onBlur={onBlur}
                                    ref={ref}
                                    placeholder="National ID Number"
                                    isDisabled={props.disableForm}
                                  />
                                  <Dropdown
                                    placeholder="Select Country..."
                                    active={
                                      (form.watch(`beneficialOwners.${index}.nationalId.countryCode`) as string) ?? ''
                                    }
                                    onChange={(val: string) =>
                                      form.setValue(`beneficialOwners.${index}.nationalId.countryCode`, val)
                                    }
                                    options={countries
                                      .filter((c) => c.code !== 'US' && c.code !== 'CA')
                                      .map((c) => ({ label: c.name, value: c.code }))}
                                    fullWidth
                                    isDisabled={props.disableForm}
                                  />
                                </Grid>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.isControlPerson`,
                              label: 'Is this person a control person?',
                              children: ({ value, onChange }) => (
                                <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                                  <OptionGroup.Option
                                    isActive={value === true}
                                    onClick={() => onChange(true)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>Yes</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                  <OptionGroup.Option
                                    isActive={value === false}
                                    onClick={() => onChange(false)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>No</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                </OptionGroup>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.isBeneficialOwner`,
                              label: 'Is this person a beneficial owner?',
                              children: ({ value, onChange }) => (
                                <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                                  <OptionGroup.Option
                                    isActive={value === true}
                                    onClick={() => onChange(true)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>Yes</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                  <OptionGroup.Option
                                    isActive={value === false}
                                    onClick={() => onChange(false)}
                                    isDisabled={props.disableForm}
                                  >
                                    <OptionGroup.Option.Radio />
                                    <OptionGroup.Option.Label>No</OptionGroup.Option.Label>
                                  </OptionGroup.Option>
                                </OptionGroup>
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.ownershipPercentage`,
                              label: 'Ownership Percentage',
                              hide: Boolean(form.watch(`beneficialOwners.${index}.isBeneficialOwner`) === false),
                              children: ({ value, onChange }) => (
                                <Input
                                  value={value !== undefined && value !== null ? String(value) : ''}
                                  onChange={onChange}
                                  placeholder="Ownership Percentage"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.jobTitle`,
                              label: 'Job Title',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Job Title"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                          ],
                        },
                        {
                          headline: 'Address',
                          fields: [
                            {
                              id: 'address',
                              label: 'Search Address',
                              children: () => (
                                <AddressField
                                  onChange={(a) => {
                                    form.setValue(`beneficialOwners.${index}.address.line1`, a.line1);
                                    form.setValue(`beneficialOwners.${index}.address.line2`, a.line2);
                                    form.setValue(`beneficialOwners.${index}.address.city`, a.city);
                                    form.setValue(`beneficialOwners.${index}.address.state`, a.state);
                                    form.setValue(`beneficialOwners.${index}.address.postalCode`, a.postalCode);
                                    form.setValue(`beneficialOwners.${index}.address.countryCode`, a.countryCode);
                                    setBeneficialOwnerAddressSearchValues((prev) => {
                                      const next = [...prev];
                                      next[index] = '';
                                      return next;
                                    });
                                    if (!a.state) {
                                      beneficialOwnerStateRef.current?.open();
                                    }
                                  }}
                                  justUS
                                  isDisabled={props.disableForm}
                                  value={beneficialOwnerAddressSearchValues[index] || ''}
                                  onValueChange={(val) =>
                                    setBeneficialOwnerAddressSearchValues((prev) => {
                                      const next = [...prev];
                                      next[index] = val;
                                      return next;
                                    })
                                  }
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.address.line1`,
                              label: 'Line 1',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Line 1"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.address.line2`,
                              label: 'Line 2',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Line 2"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.address.city`,
                              label: 'City',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="City"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.address.state`,
                              label: 'State',
                              children: ({ value, onChange }: any) => {
                                const { getValues } = useFormContext();
                                const countryCode = getValues(`beneficialOwners.${index}.address.countryCode`);
                                const filteredStates = states.filter(
                                  (s) => !countryCode || s.country_code === countryCode
                                );
                                return (
                                  <Dropdown
                                    fullWidth
                                    search
                                    placeholder="State"
                                    ref={beneficialOwnerStateRef}
                                    active={value}
                                    isDisabled={props.disableForm}
                                    onChange={onChange}
                                    options={filteredStates.map((s) => ({
                                      label: `${s.name} (${s.state_code})`,
                                      value: s.state_code,
                                    }))}
                                  />
                                );
                              },
                            },
                            {
                              id: `beneficialOwners.${index}.address.postalCode`,
                              label: 'Postal Code',
                              children: ({ value, onChange }) => (
                                <Input
                                  value={(value as string) ?? ''}
                                  onChange={onChange}
                                  placeholder="Postal Code"
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                            {
                              id: `beneficialOwners.${index}.address.countryCode`,
                              label: 'Country',
                              children: ({ value, onChange }) => (
                                <Dropdown
                                  fullWidth
                                  search
                                  placeholder="Country"
                                  active={typeof value === 'string' ? value : ''}
                                  onChange={onChange}
                                  options={countries.map((c) => ({ label: c.name, value: c.code }))}
                                  isDisabled={props.disableForm}
                                />
                              ),
                            },
                          ],
                        },
                      ]}
                    />
                  </Inner>
                </ToggleHeight>
              </Card>
            );
          })}
        </Flex>
      </Inner>
      {!props.disableForm && (
        <Inner pt={0} px="var(--page-margin)">
          <Button
            onClick={() => {
              append(createDefaultBeneficialOwner());
              setBeneficialOwnerOpen([fields.length]);
              setTimeout(() => {
                const ref = beneficialOwnerFirstFieldRefs.current[fields.length];
                ref?.focus();
                if (ref && 'focus' in ref && 'scrollIntoView' in ref && typeof ref.scrollIntoView === 'function') {
                  ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 0);
            }}
            size="small"
            variant="secondary"
            icon={<Icon.Plus />}
          >
            Add Beneficial Owner
          </Button>
        </Inner>
      )}
    </>
  );
};
